import { z } from 'zod';
import {
  defaultReferralOrder,
  HiddenReferralQuestions,
  HideableReferralQuestion,
  ReferralFormCopy,
  ReferralFormOrderInput,
} from '../referral-form';
import { DEFAULT_STAGE_MAPPING, StageMapping } from '../applicant-stage';
import { DEFAULT_DUPLICATE_THRESHOLDS, DuplicateThresholds } from '../duplicates';
import { MAX_GRADE_LABEL } from './grade-label';

export { MAX_GRADE_LABEL };

export const MAX_APPLYING_GRADES = 60;
export const DEFAULT_APPLYING_GRADES = ['PK3', 'PK4', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
export const DEFAULT_ALLOTMENT_LIMIT = 2;

export function defaultReferralSettings(): ReferralSettings {
  return {
    allotmentLimit: DEFAULT_ALLOTMENT_LIMIT,
    applyingGrades: [...DEFAULT_APPLYING_GRADES],
    formCopy: {},
    formOrder: defaultReferralOrder(),
    hiddenQuestions: [],
    duplicateThresholds: { ...DEFAULT_DUPLICATE_THRESHOLDS },
    stageMapping: DEFAULT_STAGE_MAPPING.map((e) => ({ ...e })),
  };
}

export const ApplyingGrade = z.string().trim().min(1, 'Grade label is required').max(MAX_GRADE_LABEL, `Grade label must be at most ${MAX_GRADE_LABEL} characters`);

export const ApplyingGrades = z
  .array(ApplyingGrade)
  .min(1, 'Keep at least one grade')
  .max(MAX_APPLYING_GRADES, `At most ${MAX_APPLYING_GRADES} grades`)
  .superRefine((grades, ctx) => {
    const seen = new Set<string>();
    grades.forEach((g, i) => {
      const key = g.toLowerCase();
      if (seen.has(key)) ctx.addIssue({ code: 'custom', path: [i], message: `Duplicate grade "${g}"` });
      seen.add(key);
    });
  });

export const ReferralSettings = z.object({
  allotmentLimit: z.number().int().min(1).max(20),
  applyingGrades: ApplyingGrades,
  formCopy: ReferralFormCopy,
  formOrder: ReferralFormOrderInput,
  hiddenQuestions: HiddenReferralQuestions,
  stageMapping: StageMapping,
  duplicateThresholds: DuplicateThresholds,
});
export type ReferralSettings = z.infer<typeof ReferralSettings>;

export const UpdateReferralSettings = ReferralSettings.partial().refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Nothing to update' });
export type UpdateReferralSettings = z.infer<typeof UpdateReferralSettings>;

export const RenameApplyingGradeInput = z
  .object({ from: z.string().min(1).max(MAX_GRADE_LABEL), to: ApplyingGrade })
  .refine((v) => v.from !== v.to, { path: ['to'], message: 'Choose a different label' });
export type RenameApplyingGradeInput = z.infer<typeof RenameApplyingGradeInput>;

export const RenameApplyingGradeResult = z.object({
  settings: ReferralSettings,
  applicantsUpdated: z.number().int(),
});
export type RenameApplyingGradeResult = z.infer<typeof RenameApplyingGradeResult>;

export const ApplyingGradeUsage = z.object({
  grades: z.array(z.object({ grade: z.string(), records: z.number().int() })),
});
export type ApplyingGradeUsage = z.infer<typeof ApplyingGradeUsage>;

export const AdmissionStatusUsage = z.object({
  statuses: z.array(z.object({ status: z.string(), applicants: z.number().int() })),
});
export type AdmissionStatusUsage = z.infer<typeof AdmissionStatusUsage>;

export const ReferralCycleOption = z.object({
  id: z.string().uuid(),
  code: z.string(),
  label: z.string(),
  endsOn: z.string(),
});
export type ReferralCycleOption = z.infer<typeof ReferralCycleOption>;

export const ReferralFormDto = z.object({
  applyingGrades: z.array(z.string()),
  allotmentLimit: z.number().int(),
  cycles: z.array(ReferralCycleOption),
  formCopy: ReferralFormCopy,
  formOrder: ReferralFormOrderInput,
  hiddenQuestions: z.array(HideableReferralQuestion),
});
export type ReferralFormDto = z.infer<typeof ReferralFormDto>;
